/**
 * @fileoverview Live password-policy checklist and confirm-password match hint.
 * @description Shared by every form that has the user choose a new password — SetPasswordForm
 * (accept-invitation) and ChangePasswordForm (Settings modal and the forced change after sign-in)
 * — so both show the exact same rules, driven by the real GET /identity/password-policy response
 * rather than a hardcoded copy. The met/unmet flag for each rule comes from the same
 * `evaluatePasswordPolicy` the forms' schemas validate submit against, so the checklist can never
 * show a password as fully compliant that submit would still reject.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import {
  evaluatePasswordPolicy,
  type PasswordPolicyRuleKey,
} from '@/features/auth/utils/password-policy.utils'

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

interface PasswordPolicyChecklistProps {
  policy: PasswordPolicy | undefined
  value: string
  'data-testid'?: string
}

/**
 * @description Renders the live rule checklist, one row per rule the policy actually requires.
 * Renders nothing until the policy has loaded.
 * @param {PasswordPolicyChecklistProps} props - The policy and the password typed so far.
 * @param {PasswordPolicy | undefined} props.policy - The fetched password policy, once loaded.
 * @param {string} props.value - The current value of the new-password field.
 * @param {string} [props['data-testid']] - QA locator for the list; each rule row gets the same id
 * suffixed with the rule key (e.g. reset-password-policy-checklist-upper).
 */
export function PasswordPolicyChecklist({
  policy,
  value,
  'data-testid': testId,
}: PasswordPolicyChecklistProps) {
  const { t } = useTranslation()

  if (!policy) {
    return null
  }

  const labels: Record<PasswordPolicyRuleKey, string> = {
    length: t('acceptInvitation.rules.length', { count: policy.minLength }),
    maxLength: t('acceptInvitation.rules.maxLength', { count: policy.maxLength }),
    upper: t('acceptInvitation.rules.upper'),
    lower: t('acceptInvitation.rules.lower'),
    digit: t('acceptInvitation.rules.digit'),
    special: t('acceptInvitation.rules.special'),
  }
  const rules = evaluatePasswordPolicy(policy, value)

  if (rules.length === 0) {
    return null
  }

  return (
    <ul
      data-testid={testId}
      className="mt-3 grid grid-cols-1 gap-2 rounded-[10px] border border-input-border bg-surface-muted p-3 sm:grid-cols-2"
    >
      {rules.map((rule) => (
        <li
          key={rule.key}
          data-testid={testId ? `${testId}-${rule.key}` : undefined}
          className={`flex items-center gap-2 text-xs ${rule.met ? 'text-green-700' : 'text-slate'}`}
        >
          <span
            className={`h-[15px] w-[15px] flex-shrink-0 rounded-full ${rule.met ? 'bg-green-600' : 'bg-input-border'}`}
            aria-hidden="true"
          />
          {labels[rule.key]}
        </li>
      ))}
    </ul>
  )
}

interface PasswordMatchHintProps {
  newPassword: string
  confirmPassword: string
  'data-testid'?: string
}

/**
 * @description Renders the "passwords match"/"don't match" hint once the confirm field has a
 * value.
 * @param {PasswordMatchHintProps} props - The new and confirm field values.
 * @param {string} props.newPassword - The current value of the new-password field.
 * @param {string} props.confirmPassword - The current value of the confirm-password field.
 * @param {string} [props['data-testid']] - QA locator for the hint.
 */
export function PasswordMatchHint({
  newPassword,
  confirmPassword,
  'data-testid': testId,
}: PasswordMatchHintProps) {
  const { t } = useTranslation()

  if (confirmPassword.length === 0) {
    return null
  }

  const matches = confirmPassword === newPassword

  return (
    <p
      data-testid={testId}
      className={`mt-1.5 text-xs ${matches ? 'text-green-700' : 'text-red-600'}`}
    >
      {matches ? t('acceptInvitation.passwordsMatch') : t('acceptInvitation.passwordsDontMatch')}
    </p>
  )
}
